import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import {
    useAddWidgetsToDashboardMutation,
    useGetDashboardByIdQuery,
    useUpdateDashboardMutation,
} from '@fiji/common/src/features/dashboardManagement/dashboardApi';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { useNavigationBack } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

type ReturnProps = {
    handleSubmit: (id?: any) => Promise<void>;
    redirectionHandler: () => void;
    isLoading: boolean;
    updateLoader: boolean;
    dashboard: any;
    handleSaveAs: (args0: any) => void;
};

export const useSetDashboard = (): ReturnProps => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { dashboardId, assignId } = useParams();
    const navigate = useNavigate();
    const navigationBack = useNavigationBack();
    const currentOrg = useTypedSelector(selectedOrg);

    const layout = useTypedSelector((rootState) => rootState.dashboard.layout);
    const widgets = useTypedSelector((rootState) => rootState.dashboard.widgetList);
    const currentRealmName = useTypedSelector((rootState) => rootState.common.selectedRealm);

    const handleNavigateBack = (): void => {
        navigationBack();
    };

    const {
        currentData: dashboard,
        isLoading: dashboardLoader,
        isFetching,
    }: any = useGetDashboardByIdQuery(dashboardId, {
        skip: !dashboardId || !currentOrg.id,
        refetchOnMountOrArgChange: true,
    });

    const [updateDashboard, { isLoading: updateDashboardLoader }] = useUpdateDashboardMutation();
    const [addWidgetsToDashboard, { isLoading }] = useAddWidgetsToDashboardMutation();

    function redirectionHandler(): void {
        handleNavigateBack();
    }

    async function handleSubmit(dashboardData?: any): Promise<void> {
        const { error }: any = await updateDashboard({
            body: {
                widgetLayout: layout,
            },
            params: { dashboardId: dashboardData?.id ?? dashboardId },
        });
        if (!error) {
            const initialWidgets = dashboard?.data?.widgetIdList ?? [];
            const updatedWidgets = widgets.map((widget: any) => widget?.id) ?? [];
            const removedWidgets = initialWidgets.filter((widget: string) => !updatedWidgets.includes(widget));
            const addedWidgets = updatedWidgets.filter((widget: string) => !initialWidgets.includes(widget));
            let addOrRemoveWidgetError: any;
            if (addedWidgets?.length) {
                const { error: addError }: any = await addWidgetsToDashboard({
                    action: 'ADD',
                    dashboardId: dashboardData?.id ?? dashboardId,
                    widgetIdList: addedWidgets,
                });
                if (addError) addOrRemoveWidgetError = addError;
            }
            if (removedWidgets?.length) {
                const { error: removeError }: any = await addWidgetsToDashboard({
                    action: 'REMOVE',
                    dashboardId: dashboardData?.id ?? dashboardId,
                    widgetIdList: removedWidgets,
                });
                if (removeError) addOrRemoveWidgetError = removeError;
            }
            if (!addOrRemoveWidgetError) {
                dispatch(
                    setMessageContent({
                        isOpen: true,
                        message: t('DASHBOARD_MANAGEMENT:UPDATE_DASHBOARD_MSG', {
                            replace: {
                                name: dashboard?.data?.name,
                            },
                        }),
                    })
                );
                if (dashboardData) {
                    navigate(`/${currentRealmName}/editDashboard/${assignId}/${dashboardData?.id}`, {
                        state: {
                            assignType: dashboardData?.assignType,
                            assignId: assignId ?? dashboardData?.assignTypeIds[0],
                        },
                    });
                }
            }
        }
    }

    function handleSaveAs(ref: any): void {
        ref?.current?.handleModalAction(true, dashboard?.data);
    }

    return {
        dashboard,
        handleSubmit,
        redirectionHandler,
        isLoading: dashboardLoader || isFetching || isLoading,
        updateLoader: updateDashboardLoader,
        handleSaveAs,
    };
};
